import React, { useEffect, useState } from "react";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { useDispatch, useSelector } from "react-redux";
import { selectELSLookups, selectGetAggregateSiteAssessmentInfo, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import { getAssessmentInfo, putAssessmentInfo } from "../../../redux/actions/assessmentInfo/assessmentInfo";
import { GetElsLookupsState } from "../../../redux/reducers/getLookups/getElsLookupsReducer";
import { getElsLookups } from "../../../redux/actions/lookups/lookups";
import NumberInput from "../../common/NumberInput/NumberInput";
import Dropdown, { Option } from "../../common/Dropdown/Dropdown";
import Button from "../../common/Button/Button";
import RadioButton from "../../common/RadioGroup/RadioButton";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import "./LicensedPonds.css";
import { AggregateSiteAssessmentInfo, AggregateSitePond, AggregateSiteWaterExtraction, defaultAggregateSiteAnalysisTypes, defaultAggregateSiteAssessmentInfo, defaultAggregateSitePondInfo } from "../../../types/assessment_info/aggregateSites/AggregateSiteData";
import { AggregateSiteAssessmentInfoState } from "../../../types/assessment_info/AggregateSiteAssessmentInfoState";
import DataTable from "../../common/DataTable/DataTable";
import { exactMatcher, getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import { calculateHalo, convertToAcres, convertToFeet } from "../../../utils/convertUomToImperial";
import i18n from "../../../i18n";
import { useNavigation } from "../../../utils/context/NavigationContext";
import ErrorMessages from "../../common/ErrorMessages/ErrorMessages";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../common/Modal/ConfirmationDialog";

export default function LicensedPonds(): JSX.Element {
    const dispatch = useDispatch();
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const assessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo)
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string;
    const [isUpdateReqSent, setIsUpdateReqSent] = useState(false);
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const [assessmentInfo, setAssessmentInfo] = useState(defaultAggregateSiteAssessmentInfo);
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const { t } = useTranslation("licensed_ponds");
    const analysisFlagOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["rentAnalysisOptions"] as Option[]) ?? []
    const unitTypeOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["areaUomOptions"] as Option[]) ?? []
    const perimeterUnitTypeOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["linearUomOptions"] as Option[]) ?? []
    const pondTypeOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["aggregatePondTypeOptions"] as Option[]) ?? []
    const pondStatusOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["aggregatePondStatusOptions"] as Option[]) ?? []
    const aggregateWaterExtractionLocationOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["aggregateWaterExtractionLocationOptions"] as Option[]) ?? []
    const profileType = profileOverview.profile.profile_type_code;
    const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false;
    const { isReadOnly } = useReadOnly()
    const { errors, removeError, clearErrors } = useFormErrorContext();
    const { direction } = useNavigation()
    const [dataError, setDataError] = useState(false)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [tempAreaUom, setTempAreaUom] = useState<string | null>(null)
    const [tempPerimeterUom, setTempPerimeterUom] = useState<string | null>(null)
    const [uomChanged, setUomChanged] = useState<string | null>(null)

    useEffect(() => {
        if (!elsLookupsState.dropdowns) {
            const propertyCode = sessionStorage.getItem("propertyCode") ?? "";
            dispatch(getElsLookups(propertyCode))
        }
    }, [elsLookupsState.dropdowns, dispatch]);

    useEffect(() => {
        if (currentSnapshotId && profileOverview.profile.profile_type_code && !assessmentInfoState.loading
            && (!assessmentInfoState.success || currentSnapshotId !== assessmentInfoState.snapshot_id)) {
            if (!dataError) {
                dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code))
                if (!assessmentInfoState.success && assessmentInfoState.error) {
                    toast.error(assessmentInfoState.message || "Failed to find assessment info with snapshot id");
                    setDataError(true)
                }
            }
        }
    }, [assessmentInfoState, currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, dataError]);

    useEffect(() => {
        if (saveData.saving) {
            clearErrors()
            const triggerSpectral = isInternalUser || direction !== "backward"
            let spectralSection = null
            if (profileType === "AGS") {
                spectralSection = "ags-licensed-ponds"
            }
            let pond = assessmentInfo["pond"] || defaultAggregateSitePondInfo
            if (pond) {
                const pondTypes = pond["pondTypes"]
                pondTypes?.forEach(e => e.componentId = String(crypto.randomUUID()))
                const waterExtractions = pond["waterExtractions"]
                waterExtractions?.forEach(e => e.componentId = String(crypto.randomUUID()))
                pond = { ...pond, pondTypes: pondTypes, waterExtractions: waterExtractions }
            }
            const data = {
                licenseOperationTypeCode: assessmentInfo["licenseOperationTypeCode"],
                licenseActiveFlag: assessmentInfo["licenseActiveFlag"],
                licenses: assessmentInfo["licenses"],
                surrenderRelinquishLicenseFlag: assessmentInfo["surrenderRelinquishLicenseFlag"],
                surrenderRelinquishLicenseDate: assessmentInfo["surrenderRelinquishLicenseDate"],
                rehabilitatedLicensedLandFlag: assessmentInfo["rehabilitatedLicensedLandFlag"],
                unlicensedLandFlag: assessmentInfo["unlicensedLandFlag"],
                licensedPondFlag: assessmentInfo["licensedPondFlag"],
                belowWaterExtractionFlag: assessmentInfo["belowWaterExtractionFlag"],
                attachments: assessmentInfo["attachments"],
                land: assessmentInfo["land"],
                pond: pond,
                analysisTypes: assessmentInfo["analysisTypes"],
                licensedLands1: assessmentInfo["licensedLands1"],
                licensedLands2: assessmentInfo["licensedLands2"],
                licensedLands3: assessmentInfo["licensedLands3"],
                unlicensedLands: assessmentInfo["unlicensedLands"]
            }
            dispatch(putAssessmentInfo(currentSnapshotId, {
                "data": { ...data },
                "spectral_section": spectralSection,
                "trigger_spectral": triggerSpectral,
                "profile_type_code": "AGS",
                "is_internal": isInternalUser
            }));
            setIsUpdateReqSent(true);
        }
    }, [currentSnapshotId, dispatch, saveData.saving, assessmentInfo, profileType, clearErrors, direction, isInternalUser]);

    useEffect(() => {
        if (isUpdateReqSent && !saveResultState.loading) {
            if (saveResultState.status === 200) {
                dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
            }
            setIsUpdateReqSent(false);
        }
    }, [currentSnapshotId, dispatch, isUpdateReqSent, profileOverview.profile.profile_type_code, saveResultState]);

    useEffect(() => {
        let data = { ...assessmentInfo }
        if (Object.keys(assessmentInfoState.formData).length !== 0) {
            data = { ...assessmentInfoState.formData as AggregateSiteAssessmentInfo }
        }
        if (!data["analysisTypes"] || data["analysisTypes"]?.length === 0) {
            data["analysisTypes"] = defaultAggregateSiteAnalysisTypes
        }
        data["analysisTypes"].forEach(e => e.componentId = String(crypto.randomUUID()))
        setAssessmentInfo(data)
        setTempAreaUom(data.pond?.areaUom as string)
        setTempPerimeterUom(data.pond?.perimeterUom as string)
        // eslint-disable-next-line
    }, [assessmentInfoState, elsLookupsState])

    const addPond = (type: string) => {
        const pond = assessmentInfo.pond
        if (type === "pond") {
            if (pond?.pondTypes && pond.pondTypes.length < 5) {
                const pondTypes = pond.pondTypes
                pondTypes.push({
                    id: String(crypto.randomUUID()),
                    pondCategoryCode: "L",
                    pondTypeCode: "",
                    statusCode: "",
                    area: null,
                    areaSys: null,
                    inletPumpCount: null,
                    componentId: String(crypto.randomUUID())
                })
            }
        } else {
            if (pond?.waterExtractions && pond.waterExtractions.length < 5) {
                const waterExtractions = pond.waterExtractions
                waterExtractions.push({
                    id: String(crypto.randomUUID()),
                    locationCode: "",
                    area: null,
                    areaSys: null,
                    perimeter: null,
                    perimeterSys: null,
                    waterHalo: null,
                    waterHaloSys: null,
                    componentId: String(crypto.randomUUID())
                })
            }
        }
        setAssessmentInfo({ ...assessmentInfo, pond: pond })
    }

    const convertUom = (uom: string) => {
        const unitType = unitTypeOptions.find(o => o.value === uom)
        return i18n.language === 'fr' ? unitType?.frenchText : unitType?.text
    }

    const convertPerimeterUom = (uom: string) => {
        const unitType = perimeterUnitTypeOptions.find(o => o.value === uom)
        return i18n.language === 'fr' ? unitType?.frenchText : unitType?.text
    }

    const convertHaloUom = (uom: string) => {
        const areaUomOption = unitTypeOptions.find(o => o.value === uom);
        if (areaUomOption) {
            return i18n.language === 'fr' ? areaUomOption?.frenchText : areaUomOption?.text
        }
        else {
            return ""
        }
    }

    const pondHeadings = [
        <th className="pond-table-heading-1 text-center" key="column-1-header">{t("pondType")}</th>,
        <th className="pond-table-heading-1 text-center" key="column-2-header">{t("status")}</th>,
        <th className="pond-table-heading-1 text-center" key="column-3-header">{t("area")} {assessmentInfo.pond?.areaUom as string ? "(" + convertUom(assessmentInfo.pond?.areaUom as string) + ")" : null}</th>,
        <th className="pond-table-heading-1 text-center" key="column-5-header">{t("numberOfInletPumps")}</th>,
        <th className="pond-table-heading-2 text-center" key="column-6-header"></th>
    ]

    const waterExtractionHeadings = [
        <th className="pond-table-heading-1 text-center" key="column-7-header">{t("location")}</th>,
        <th className="pond-table-heading-1 text-center" key="column-8-header">{t("inputArea")} {assessmentInfo.pond?.areaUom as string ? "(" + convertUom(assessmentInfo.pond?.areaUom as string) + ")" : null}</th>,
        <th className="pond-table-heading-1 text-center" key="column-9-header">{t("inputPerimeter")} {assessmentInfo.pond?.perimeterUom as string ? "(" + convertPerimeterUom(assessmentInfo.pond?.perimeterUom as string) + ")" : null}</th>,
        <th className="pond-table-heading-1 text-center" key="column-10-header">{t("halo")} {assessmentInfo.pond?.perimeterUom as string ? "(" + convertHaloUom(assessmentInfo.pond?.perimeterUom as string) + ")" : null}</th>,
        <th className="pond-table-heading-2 text-center" key="column-11-header"></th>
    ]

    const buildLicensedPondTableContent = () => {
        const tableBody: JSX.Element[] = [];
        const pond = assessmentInfo?.pond
        const ponds = pond?.pondTypes
        if (ponds && ponds.length) {
            for (let i = 0; i < ponds.length; i++)
                tableBody.push(<tr key={i}>
                    <td>
                        <Dropdown options={pondTypeOptions}
                            onChange={(e) => {
                                ponds[i].pondTypeCode = (e.target as HTMLSelectElement).value
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".pondTypeCode")?.primaryKey as string)
                            }}
                            placeholder={true}
                            name="pond-type" ariaLabel="Pond Type" value={ponds[i].pondTypeCode}
                            className={"width-100" + (hasError(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId + ".pondTypeCode", exactMatcher) ? "ags-table-error" : "")}
                            hideErrorCross
                            errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".pondTypeCode", exactMatcher)} />
                    </td>
                    <td className={"pond-status-radio-button-cell"}>
                        <Dropdown options={pondStatusOptions}
                            onChange={(e) => {
                                ponds[i].statusCode = (e.target as HTMLSelectElement).value
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".statusCode")?.primaryKey as string)
                            }}
                            placeholder={true}
                            name="pond-type" ariaLabel="Pond Type" value={ponds[i].statusCode}
                            hideErrorCross
                            className={"width-100" + (hasError(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId + ".statusCode", exactMatcher) ? "ags-table-error" : "")}
                            errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".statusCode", exactMatcher)} />
                    </td>
                    <td className={`${hasError(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId + ".area", exactMatcher) ? "ags-table-error" : ""}`}>
                        <NumberInput name={"assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".area"} border={false}
                            negatives={false} decimalPlaces={2} commas={true} max={99999999.9}
                            initialValue={ponds[i].area !== null ? ponds[i].area : null}
                            onChange={(value, _last) => {
                                ponds[i].area = value
                                ponds[i].areaSys = convertToAcres(assessmentInfo.pond?.areaUom as string, value as number || null)
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".area")?.primaryKey as string)
                            }}
                            className="width-100"
                            textAlign="center"
                            errorMatcher={exactMatcher}
                        />
                    </td>
                    <td className={ponds[i].pondTypeCode === "2" ? "" : "background-grey" + (hasError(errors, "assessmentInfo.pond.pondTypes." + ponds[i].componentId + ".inletPumpCount", exactMatcher) ? " ags-table-error" : "")}>
                        {ponds[i].pondTypeCode === "2" ? <NumberInput name={"assessmentInfo.pond.pondTypes." + ponds[i].componentId as string + ".inletPumpCount"} border={false}
                            negatives={false} decimalPlaces={0} commas={true} max={99999999} size={10}
                            initialValue={ponds[i].inletPumpCount !== null ? ponds[i].inletPumpCount : null}
                            onChange={(value, _last) => {
                                ponds[i].inletPumpCount = value
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.pondTypes " + ponds[i].componentId as string + ".inletPumpCount")?.primaryKey as string)
                            }}
                            className="width-100"
                            textAlign="center"
                            errorMatcher={exactMatcher}
                        /> : <></>}
                    </td>
                    <td className="text-center">
                        <FontAwesomeIcon className={`cursor-pointer trash` + (isReadOnly ? ` read-only` : ``)} icon={faTrashCan} onClick={() => {
                            if (!isReadOnly) {
                                const pond = assessmentInfo.pond
                                if (pond) {
                                    const updatedPonds = assessmentInfo.pond?.pondTypes?.filter((pond, index) => {
                                        return pond ? index !== i : null
                                    })
                                    if (updatedPonds) {
                                        for (let i = 0; i < updatedPonds?.length; i++) {
                                            updatedPonds[i].id = "POND-0" + (i + 1).toString()
                                        }
                                    }
                                    pond.pondTypes = updatedPonds as AggregateSitePond[]
                                    setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                }
                            }
                        }} />
                    </td>
                </tr>
                )
        }
        return (<>{tableBody}</>)
    }

    const buildBelowWaterExtractionTableContent = () => {
        const tableBody: JSX.Element[] = [];
        const pond = assessmentInfo?.pond
        const waterExtractions = pond?.waterExtractions
        if (waterExtractions && waterExtractions.length) {
            for (let i = 0; i < waterExtractions.length; i++)
                tableBody.push(<tr key={i}>
                    <td><Dropdown options={aggregateWaterExtractionLocationOptions} onChange={(e) => {
                        waterExtractions[i].locationCode = (e.target as HTMLSelectElement).value
                        setAssessmentInfo({ ...assessmentInfo, pond: pond })
                        removeError(getFormErrorByName(errors, "assesmentInfo.pond" + waterExtractions[i].componentId as string + ".locationCode")?.primaryKey as string)
                    }}
                        placeholder={true}
                        name="pond-type" ariaLabel="Pond Type" value={waterExtractions[i].locationCode}
                        hideErrorCross
                        className={"width-100" + (hasError(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId + ".locationCode", exactMatcher) ? "ags-table-error" : "")}
                        errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId as string + ".locationCode", exactMatcher)}
                    /></td>
                    <td className={`${hasError(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId + ".area", exactMatcher) ? "ags-table-error" : ""}`}>
                        <NumberInput name={"assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId as string + ".area"} border={false}
                            negatives={false} decimalPlaces={2} commas={true} max={99999999.9}
                            initialValue={waterExtractions[i].area !== null ? waterExtractions[i].area : null}
                            onChange={(value, _last) => {
                                waterExtractions[i].area = value
                                waterExtractions[i].areaSys = convertToAcres(assessmentInfo.pond?.areaUom as string, value as number || null)
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId as string + ".area")?.primaryKey as string)
                            }}
                            className="width-100"
                            textAlign="center"
                            errorMatcher={exactMatcher}
                        />
                    </td>
                    <td className={`${hasError(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId + ".area", exactMatcher) ? "ags-table-error" : ""}`}>
                        <NumberInput name={"assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId as string + ".perimeter"} border={false}
                            negatives={false} decimalPlaces={2} commas={true} max={99999999.9}
                            initialValue={waterExtractions[i].perimeter !== null ? waterExtractions[i].perimeter : null}
                            onChange={(value, _last) => {
                                waterExtractions[i].perimeter = value
                                waterExtractions[i].perimeterSys = convertToFeet(assessmentInfo.pond?.perimeterUom as string, value as number)
                                waterExtractions[i].waterHalo = calculateHalo(value as number, assessmentInfo.pond?.perimeterUom as string)
                                waterExtractions[i].waterHaloSys = convertToFeet(assessmentInfo.pond?.perimeterUom as string, waterExtractions[i].waterHalo as number)
                                setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                removeError(getFormErrorByName(errors, "assessmentInfo.pond.waterExtractions." + waterExtractions[i].componentId as string + ".perimeter")?.primaryKey as string)
                            }}
                            className="width-100"
                            textAlign="center"
                            errorMatcher={exactMatcher}
                        />
                    </td>
                    <td className="background-grey text-center">
                        {waterExtractions[i].waterHalo?.toFixed(2)}
                    </td>
                    <td className="text-center">
                        <FontAwesomeIcon className={`cursor-pointer trash` + (isReadOnly ? ` read-only` : ``)} icon={faTrashCan} onClick={() => {
                            if (!isReadOnly) {
                                const pond = assessmentInfo.pond
                                if (pond) {
                                    const updatedPonds = assessmentInfo.pond?.waterExtractions?.filter((pond, index) => {
                                        return pond ? index !== i : null
                                    })
                                    if (updatedPonds) {
                                        for (let i = 0; i < updatedPonds?.length; i++) {
                                            updatedPonds[i].id = "BWE-0" + (i + 1).toString()
                                        }
                                    }
                                    pond.waterExtractions = updatedPonds as AggregateSiteWaterExtraction[]
                                    setAssessmentInfo({ ...assessmentInfo, pond: pond })
                                }
                            }
                        }} />
                    </td>
                </tr>
                )
        }
        return (<>{tableBody}</>)
    }

    const handleUomChange = (uom: string, uomChanged: string) => {
        if (uomChanged === "area") {
            const unitType = unitTypeOptions.find(o => o.value === uom)
            if (unitType) {
                const pond = assessmentInfo.pond
                if (pond) {
                    pond.areaUom = uom
                    const pondTypes = pond["pondTypes"]
                    if (pondTypes) {
                        (pondTypes as AggregateSitePond[])?.forEach((e) => {
                            if (e.area) {
                                e.area = null
                                e.areaSys = null
                            }
                        })
                        setAssessmentInfo({ ...assessmentInfo, pond: pond })
                    }
                    const waterExtractions = pond["waterExtractions"]
                    if (waterExtractions) {
                        (waterExtractions as AggregateSiteWaterExtraction[])?.forEach((e) => {
                            if (e.area) {
                                e.area = null
                                e.areaSys = null
                            }
                        })
                        setAssessmentInfo({ ...assessmentInfo, pond: pond })
                    }
                }
            }
            removeError(getFormErrorByName(errors, "assessmentInfo.pond.areaUom")?.primaryKey as string)
        } else {
            const unitType = perimeterUnitTypeOptions.find(o => o.value === uom)
            if (unitType) {
                const pond = assessmentInfo.pond
                if (pond) {
                    pond.perimeterUom = uom
                    const waterExtractions = pond["waterExtractions"]
                    if (waterExtractions) {
                        (waterExtractions as AggregateSiteWaterExtraction[])?.forEach((w) => {
                            if (w.perimeter) {
                                w.perimeter = null
                                w.perimeterSys = null
                                w.waterHalo = null
                                w.waterHaloSys = null
                            }
                        })
                    }
                    setAssessmentInfo({ ...assessmentInfo, pond: pond })
                }
            }
            removeError(getFormErrorByName(errors, "assessmentInfo.pond.perimeterUom")?.primaryKey as string)
        }
    }

    return (
        <>
            <ErrorMessages errors={errors} />
            <div className="margin-bottom-1">{t("licensedPonds")}</div>
            <div className="ponds-table margin-bottom-1">
                <div>
                    <div className="inline-block margin-right-1">{t("hasPonds")}</div>
                    <RadioButton name="licensed-ponds" id="licensed-ponds-yes" text={t("yes")} textPosition="right" value="true"
                        currentValue={assessmentInfo.licensedPondFlag || ""} onChange={() => {
                            let pond = assessmentInfo.pond
                            if (!pond) {
                                pond = defaultAggregateSitePondInfo
                            }
                            let pondTypes = pond.pondTypes
                            pondTypes = [{
                                id: String(crypto.randomUUID()),
                                pondCategoryCode: "L",
                                pondTypeCode: "",
                                statusCode: "",
                                area: null,
                                areaSys: null,
                                inletPumpCount: null,
                                componentId: String(crypto.randomUUID())
                            }]
                            pond = { ...pond, pondTypes: pondTypes }
                            setAssessmentInfo({
                                ...assessmentInfo, licensedPondFlag: true, pond: pond
                            })
                            removeError(getFormErrorByName(errors, "licensedPondFlag")?.primaryKey as string)
                        }} className="inline-block margin-right-1"
                        isError={hasError(errors, "licensedPondFlag")}
                        ariaLabel="licensed-ponds" />
                    <RadioButton name="licensed-ponds" id="licensed-ponds-no" text={t("no")} textPosition="right" value="false"
                        currentValue={assessmentInfo.licensedPondFlag === false ? "false" : ""} onChange={() => {
                            const pond = assessmentInfo.pond
                            if (pond) {
                                pond.pondTypes = null
                                setAssessmentInfo({ ...assessmentInfo, licensedPondFlag: false, pond: pond })
                            }
                            removeError(getFormErrorByName(errors, "licensedPondFlag")?.primaryKey as string)
                        }} className="inline-block"
                        isError={hasError(errors, "licensedPondFlag")}
                        ariaLabel="licensed-ponds" />
                </div>
                {assessmentInfo["licensedPondFlag"] ?
                    <div>
                        <span className="inline-block ags-input-label">{t("identifyUom")}</span>
                        <span className="inline-block"><Dropdown options={unitTypeOptions}
                            onChange={(e) => {
                                if (!tempAreaUom) {
                                    handleUomChange((e.target as HTMLSelectElement).value, "area")
                                } else {
                                    setUomChanged("area")
                                    setShowConfirmation(true)
                                }
                                setTempAreaUom((e.target as HTMLSelectElement).value)
                            }}
                            name="unit-type-options" ariaLabel="Unit type options" value={assessmentInfo.pond?.areaUom || ""} placeholder={true}
                            hideErrorCross
                            errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.pond.areaUom", exactMatcher)}
                            className={"uom-dropdown" + (hasError(errors, "assessmentInfo.pond.areaUom", exactMatcher) ? " ags-table-error" : "")}
                        /></span>
                        <div>
                            <div className="inline-block">{t("typesOfPonds")}</div>
                            {!isReadOnly ? <div className='suite-breakdown-btn-container inline-block add-button'>
                                <Button onClick={() => addPond("pond")} text={t("add") as string} ariaLabel="add-pond" />
                            </div> : <></>}
                        </div>
                        <DataTable
                            headings={pondHeadings}
                            className={"assisted-suite-breakdown-table"}>
                            {buildLicensedPondTableContent()}
                        </DataTable>
                    </div>
                    : <></>
                }
                <div className="margin-top-1">
                    <div className="inline-block margin-right-1">{t("hasBelowWaterExtraction")}</div>
                    <RadioButton name="below-water-extraction" id="below-water-extraction-yes" text={t("yes")} textPosition="right" value="true"
                        currentValue={assessmentInfo.belowWaterExtractionFlag || ""} onChange={() => {
                            let pond = assessmentInfo.pond
                            if (!pond) {
                                pond = defaultAggregateSitePondInfo
                            }
                            let waterExtractions = pond.waterExtractions
                            waterExtractions = [{
                                id: String(crypto.randomUUID()),
                                locationCode: "",
                                area: null,
                                areaSys: null,
                                perimeter: null,
                                perimeterSys: null,
                                waterHalo: null,
                                waterHaloSys: null,
                                componentId: String(crypto.randomUUID())
                            }]
                            pond = { ...pond, waterExtractions: waterExtractions }
                            setAssessmentInfo({
                                ...assessmentInfo, "belowWaterExtractionFlag": true, pond: pond
                            })
                            removeError(getFormErrorByName(errors, "belowWaterExtractionFlag")?.primaryKey as string)
                        }} className="inline-block margin-right-1"
                        isError={hasError(errors, "belowWaterExtractionFlag")}
                        ariaLabel="below-water-extraction" />
                    <RadioButton name="below-water-extraction" id="below-water-extraction-no" text={t("no")} textPosition="right" value="false"
                        currentValue={assessmentInfo.belowWaterExtractionFlag === false ? "false" : ""} onChange={() => {
                            const pond = assessmentInfo.pond
                            if (pond) {
                                pond.waterExtractions = null
                                setAssessmentInfo({ ...assessmentInfo, belowWaterExtractionFlag: false, pond: pond })
                            }
                            removeError(getFormErrorByName(errors, "belowWaterExtractionFlag")?.primaryKey as string)
                        }} className="inline-block"
                        isError={hasError(errors, "belowWaterExtractionFlag")}
                        ariaLabel="below-water-extraction" />
                </div>
                <div>
                    {assessmentInfo["belowWaterExtractionFlag"] ?
                        <div>
                            <span className="inline-block ags-input-label">{t("identifyUom")}</span>
                            <span className="inline-block"><Dropdown options={perimeterUnitTypeOptions}
                                onChange={(e) => {
                                    if (!tempPerimeterUom) {
                                        handleUomChange((e.target as HTMLSelectElement).value, "perimeter")
                                    } else {
                                        setUomChanged("perimeter")
                                        setShowConfirmation(true)
                                    }
                                    setTempPerimeterUom((e.target as HTMLSelectElement).value)
                                }}
                                name="unit-type-options" ariaLabel="Unit type options" value={assessmentInfo.pond?.perimeterUom || ""} placeholder={true}
                                hideErrorCross
                                errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.pond.perimeterUom", exactMatcher)}
                                className={"uom-dropdown" + (hasError(errors, "assessmentInfo.pond.perimeterUom", exactMatcher) ? " ags-table-error" : "")} /></span>
                            <div>
                                <div className="inline-block">{t("locationAcreagePerimeter")}</div>
                                {!isReadOnly ? <div className='suite-breakdown-btn-container inline-block add-button'>
                                    <Button onClick={() => addPond("waterExtractions")} text={t("add") as string} ariaLabel="add-water-extraction" />
                                </div> : <></>}
                            </div>
                            <DataTable
                                headings={waterExtractionHeadings}
                                className={"assisted-suite-breakdown-table"}>
                                {buildBelowWaterExtractionTableContent()}
                            </DataTable>
                        </div> : <></>}
                </div>
            </div>
            {profileType === "AGS" && isInternalUser && sessionStorage.getItem("campaignStatusCode") === "EXTERNAL_IN_PROGRESS" && analysisFlagOptions.length > 0 &&
                <div className='airp-badge badge-lighter-suburb-yellow analysis-container'>
                    <label htmlFor='analysisFlag'>{t("analysis")}</label>
                    <div className='reviewed-input-container'>
                        <div className='reviewed-check-container'>
                            <Dropdown id="analysisFlag" name='analysisFlag' className='admin-input-style'
                                options={analysisFlagOptions}
                                value={assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "P")?.analysisFlag}
                                onChange={(e) => {
                                    const temp = assessmentInfo.analysisTypes
                                    if (temp?.length) {
                                        const idx = temp?.findIndex(e => e.analysisTypeCode === "P")
                                        temp[idx] = {
                                            ...temp[idx],
                                            "analysisFlag": (e.target as HTMLSelectElement).value,
                                            "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
                                            "analysisReviewedDate": (new Date()).toISOString()
                                        }
                                    }
                                    setAssessmentInfo({ ...assessmentInfo, analysisTypes: temp })
                                    removeError(getFormErrorByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "P")?.componentId as string)?.primaryKey as string)
                                }}
                                hideErrorCross
                                errorMessage={getFormErrorMessageByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "P")?.componentId as string, exactMatcher)}
                            />
                        </div>
                    </div>

                </div>
            }
            {showConfirmation &&
                <ConfirmationDialog
                    onCancel={() => { setShowConfirmation(false) }}
                    onConfirm={() => {
                        setShowConfirmation(false)
                        handleUomChange((uomChanged === "area" ? tempAreaUom : tempPerimeterUom) as string, uomChanged as string)
                    }}
                    headerClass="no-border" buttonGroupClass="justify-content-center no-border"
                >
                    {t("confirmation")}
                </ConfirmationDialog>
            }
        </>
    )
}
